/**
 * Runs in the top window. Wires the preview iframe to the editor state:
 *  - hover/click on a section → selects it in the sidebar
 *  - hover/click on a block inside a section → selects the block
 *  - outline on the currently selected section/block
 *  - exposes window.LCStore.designMode = true in the iframe
 *  - listens for theme JS lifecycle events
 *
 * Block detection uses the `data-block-id` attribute that the engine's
 * `{{ block.shopify_attributes }}` helper emits. Themes that don't
 * render that attribute fall back to section-level selection only.
 */

import type { Selection, Device } from '../state/store';
import type { SectionGroup } from '../api/types';

const OVERLAY_STYLE_ID = 'lc-editor-overlay-style';

const OVERLAY_CSS = `
[data-section-id] {
  position: relative;
  transition: outline-color 0.08s ease-in;
}
[data-block-id] {
  position: relative;
  transition: outline-color 0.08s ease-in;
}

/* SECTION hover — subtle. Dims when a block inside is being hovered
   so the block's own outline wins visually. */
[data-section-id]:hover {
  outline: 2px solid rgba(0, 91, 211, 0.35) !important;
  outline-offset: -2px !important;
  cursor: pointer !important;
}
[data-section-id]:has([data-block-id]:hover) {
  outline-color: rgba(0, 91, 211, 0.10) !important;
}

/* BLOCK hover — crisper than the section so the user knows the click
   will land on the block, not the wrapper. */
[data-block-id]:hover {
  outline: 2px solid rgba(0, 91, 211, 0.65) !important;
  outline-offset: -2px !important;
  cursor: pointer !important;
}

/* Selected states — solid outline + corner label pill. */
[data-section-id][data-lc-selected="true"],
[data-block-id][data-lc-selected="true"] {
  outline: 2px solid #005bd3 !important;
  outline-offset: -2px !important;
}
[data-section-id][data-lc-selected="true"]::after {
  content: attr(data-section-type);
  position: absolute;
  top: 0;
  left: 0;
  background: #005bd3;
  color: #ffffff;
  font-size: 11px;
  font-weight: 500;
  padding: 3px 10px;
  z-index: 999999;
  font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  text-transform: capitalize;
  border-radius: 0 0 6px 0;
  pointer-events: none;
  letter-spacing: 0.02em;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
}
[data-block-id][data-lc-selected="true"]::after {
  content: attr(data-block-type);
  position: absolute;
  top: 0;
  left: 0;
  background: #005bd3;
  color: #ffffff;
  font-size: 11px;
  font-weight: 500;
  padding: 3px 10px;
  z-index: 999999;
  font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  text-transform: capitalize;
  border-radius: 0 0 6px 0;
  pointer-events: none;
  letter-spacing: 0.02em;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
}

html, body { scroll-behavior: smooth; }
`;

export interface IframeHandle {
  doc: Document;
  win: Window;
}

export function getHandle(iframe: HTMLIFrameElement | null): IframeHandle | null {
  if (!iframe) return null;
  const doc = iframe.contentDocument;
  const win = iframe.contentWindow;
  if (!doc || !win) return null;
  return { doc, win };
}

export function installOverlay(handle: IframeHandle) {
  const { doc, win } = handle;
  if (doc.getElementById(OVERLAY_STYLE_ID)) return;
  const style = doc.createElement('style');
  style.id = OVERLAY_STYLE_ID;
  style.textContent = OVERLAY_CSS;
  doc.head.appendChild(style);

  // Design-mode flag
  (win as any).LCStore = (win as any).LCStore || {};
  (win as any).LCStore.designMode = true;
}

/**
 * Reverses `installOverlay`. Drops the overlay CSS + clears any selection
 * outline that was painted by it. The `LCStore.designMode` flag is left
 * in place so theme code that branches on it behaves consistently whether
 * the overlay is on or off — the flag means "we're inside the editor",
 * not "the overlay is active".
 */
export function uninstallOverlay(handle: IframeHandle) {
  const { doc } = handle;
  const style = doc.getElementById(OVERLAY_STYLE_ID);
  if (style) style.remove();
  doc.querySelectorAll('[data-lc-selected="true"]').forEach((el) => {
    (el as HTMLElement).removeAttribute('data-lc-selected');
  });
}

export function applySelection(handle: IframeHandle, selection: Selection) {
  const { doc } = handle;
  doc.querySelectorAll('[data-lc-selected="true"]').forEach((el) => {
    (el as HTMLElement).removeAttribute('data-lc-selected');
  });
  if (!selection) return;

  if (selection.kind === 'block') {
    // Try to highlight the block's own DOM node inside the section.
    // Fallback to the section if the theme didn't emit data-block-id.
    const sectionEl = doc.querySelector(
      `[data-section-id="${cssAttrEscape(selection.sectionId)}"]`
    ) as HTMLElement | null;
    const blockEl =
      sectionEl?.querySelector(
        `[data-block-id="${cssAttrEscape(selection.blockId)}"]`
      ) as HTMLElement | null;

    const target = blockEl || sectionEl;
    if (target) {
      target.setAttribute('data-lc-selected', 'true');
      scrollIntoViewIfNeeded(target);
    }
    return;
  }

  if (selection.kind === 'section') {
    const el = doc.querySelector(
      `[data-section-id="${cssAttrEscape(selection.sectionId)}"]`
    ) as HTMLElement | null;
    if (el) {
      el.setAttribute('data-lc-selected', 'true');
      scrollIntoViewIfNeeded(el);
    }
  }
}

function scrollIntoViewIfNeeded(el: HTMLElement) {
  const doc = el.ownerDocument;
  const view = doc.defaultView;
  if (!view) return;
  const rect = el.getBoundingClientRect();
  if (rect.top < 40 || rect.bottom > view.innerHeight - 40) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

function cssAttrEscape(value: string): string {
  return value.replace(/"/g, '\\"');
}

export type ClickPayload =
  | { kind: 'section'; sectionId: string; group: SectionGroup }
  | { kind: 'block'; sectionId: string; group: SectionGroup; blockId: string };

export type ClickHandler = (payload: ClickPayload) => void;

/*
  Selectors de elementos INTERATIVOS que o handler de click-to-select
  do editor NÃO deve interceptar. Quando o lojista clica nesses, o
  evento deve passar pro JS do tema (variantes, quantity, ATC, etc.).
  Sem whitelist, o `e.preventDefault() + e.stopPropagation()` em capture
  phase mata o click handler do tema antes dele rodar.

  Bug reportado: "ao clicar em uma variante nao esta acotnecendo nada
  no main-product".

  Lista deliberadamente conservadora — qualquer elemento "clicável"
  do tema entra aqui. Se o lojista quiser SELECIONAR um bloco que
  CONTÉM um botão (ex: clicar fora do botão), aí o handler dispara
  normal. Clique direto no botão deixa o tema lidar.
*/
const INTERACTIVE_SELECTOR = [
  'button',
  'input',
  'select',
  'textarea',
  'label',
  '[role="button"]',
  '[role="tab"]',
  '[role="link"]',
  '[role="checkbox"]',
  '[role="radio"]',
  '[role="switch"]',
  '[role="menuitem"]',
  '[contenteditable="true"]',
  '.variation-option',
  '.quantity-btn',
  '.swiper-slide',
  '.swiper-button-next',
  '.swiper-button-prev',
  '.swiper-pagination-bullet',
  '[data-no-editor-click]',
].join(',');

export function installClickHandler(handle: IframeHandle, onClick: ClickHandler) {
  const { doc } = handle;
  const listener = (e: MouseEvent) => {
    const t = e.target as HTMLElement;
    const sectionNode = t.closest('[data-section-id]') as HTMLElement | null;
    if (!sectionNode) return;

    // Se o click foi num <a href>, deixa o navigation-interceptor
    // lidar — o editor-mode converte a navegação em setPage/setPreview*
    // ao invés de jogar o usuário fora do editor.
    const anchor = t.closest('a[href]') as HTMLAnchorElement | null;
    if (anchor) return;

    /*
      Click em elemento interativo do tema (botão de variante, quantity,
      ATC, swiper nav, etc.) — NÃO bloqueia. Deixa o evento propagar
      pra que o JS do tema processe. O lojista pode "selecionar" o
      bloco clicando em qualquer área NÃO-interativa do bloco.
    */
    if (t.closest(INTERACTIVE_SELECTOR)) return;

    e.preventDefault();
    e.stopPropagation();

    const sectionId = sectionNode.getAttribute('data-section-id') || '';
    const group = (sectionNode.getAttribute('data-section-group') ||
      'template') as SectionGroup;

    // Prefer the closest block if one is present between the click target
    // and the section wrapper. This lets the user click into a block like
    // a variant-picker without the section swallowing the event.
    const blockNode = t.closest('[data-block-id]') as HTMLElement | null;
    if (blockNode && sectionNode.contains(blockNode)) {
      const blockId = blockNode.getAttribute('data-block-id') || '';
      if (blockId) {
        onClick({ kind: 'block', sectionId, group, blockId });
        return;
      }
    }
    onClick({ kind: 'section', sectionId, group });
  };
  doc.addEventListener('click', listener, true);

  return () => doc.removeEventListener('click', listener, true);
}

/**
 * Intercepta cliques em <a href> dentro da preview iframe e converte
 * navegação interna (home, páginas, coleções, produtos) em trocas de
 * estado do editor — assim o usuário continua navegando pela loja sem
 * sair do editor.
 *
 * Links EXTERNOS (other domain) abrem em nova aba pra não perder o
 * trabalho do usuário. Links com `target="_blank"`, âncora pura (`#`),
 * `mailto:`/`tel:` e cliques com ctrl/cmd/middle-button passam pelo
 * comportamento default.
 *
 * O callback recebe objetos estruturados (parseados do href) e quem
 * chama decide como aplicar — tipicamente chamar os setters do store
 * (`setPage`, `setPreviewProduct`, `setPreviewCollection`).
 */
export type NavigationIntent =
  | { kind: 'home' }
  | { kind: 'product'; slug: string }
  | { kind: 'collection'; id: string }
  | { kind: 'page'; handle: string }
  /**
   * Click no ícone de carrinho (header) ou em qualquer `<a href="/cart">`.
   * Em modo drawer/message o lojista espera ABRIR a gaveta — não navegar
   * pra uma página de carrinho separada. O Preview reage despachando
   * `theme:cart:open` no iframe, que o snippet drawer-cart escuta.
   * Em modo "page" cai no `setPage('cart')` (template.json do carrinho).
   */
  | { kind: 'cart' }
  | { kind: 'external'; url: string }
  | { kind: 'unknown'; href: string };

export type NavigationHandler = (intent: NavigationIntent) => void;

export function installNavigationInterceptor(
  handle: IframeHandle,
  onNavigate: NavigationHandler
) {
  const { doc } = handle;
  const listener = (e: MouseEvent) => {
    // Ignora cliques com modificadores (ctrl/cmd/shift = abrir nova aba,
    // middle-click, etc.) — respeita comportamento padrão do browser.
    if (e.defaultPrevented) return;
    if (e.button !== 0) return;
    if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;

    const anchor = (e.target as HTMLElement).closest('a[href]') as
      | HTMLAnchorElement
      | null;
    if (!anchor) return;

    const rawHref = anchor.getAttribute('href') || '';
    if (!rawHref) return;

    // Pula âncoras puras, mailto, tel, javascript:
    if (
      rawHref.startsWith('#') ||
      rawHref.startsWith('mailto:') ||
      rawHref.startsWith('tel:') ||
      rawHref.startsWith('javascript:')
    ) {
      return;
    }

    // target="_blank" ou similares → deixa abrir em nova aba
    const target = anchor.getAttribute('target');
    if (target && target !== '_self') return;

    // Resolve URL absoluta usando o href resolvido pelo browser
    // (`anchor.href` faz isso, respeitando `<base href>`).
    let url: URL;
    try {
      url = new URL(anchor.href);
    } catch {
      onNavigate({ kind: 'unknown', href: rawHref });
      return;
    }

    const pathname = url.pathname;

    // Detecta se é link interno da LOJA (engine.lunacheckout, domínio
    // do tenant). Assume que qualquer link não-externo serve — se o
    // pathname começa com `/`, trata como interno.
    const isInternal =
      rawHref.startsWith('/') ||
      url.hostname === doc.location.hostname ||
      url.hostname.endsWith('.lunacheckout.com');

    if (!isInternal) {
      // Abre externos em nova aba pra preservar editor
      e.preventDefault();
      e.stopPropagation();
      window.open(url.href, '_blank', 'noopener,noreferrer');
      onNavigate({ kind: 'external', url: url.href });
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    // Home
    if (pathname === '/' || pathname === '/index' || pathname === '') {
      onNavigate({ kind: 'home' });
      return;
    }

    /*
      Carrinho: /cart, /cart/, /carrinho, /carrinho/. Detectado ANTES
      de produto/coleção pra cobrir o ícone do header. Antes caía em
      `kind: 'unknown'` (pathname não casava com /products/* ou /pages/*),
      e o handler do Preview só logava "navegação não roteada: /cart".

      O `e.preventDefault()` mais acima já bloqueou a navegação real;
      o handler do Preview reage despachando `theme:cart:open` no
      iframe (drawer-cart.liquid escuta esse evento e abre a gaveta).
    */
    if (
      pathname === '/cart' ||
      pathname === '/cart/' ||
      pathname === '/carrinho' ||
      pathname === '/carrinho/'
    ) {
      onNavigate({ kind: 'cart' });
      return;
    }

    // Produto: /products/:slug
    const productMatch = pathname.match(/^\/products\/([^/?#]+)/);
    if (productMatch) {
      onNavigate({ kind: 'product', slug: productMatch[1] });
      return;
    }

    // Coleção: /collections/:handle-ou-id ou /collection/:id
    const collectionMatch = pathname.match(/^\/collections?\/([^/?#]+)/);
    if (collectionMatch) {
      onNavigate({ kind: 'collection', id: collectionMatch[1] });
      return;
    }

    // Página CMS: /pages/:handle
    const pageMatch = pathname.match(/^\/pages\/([^/?#]+)/);
    if (pageMatch) {
      onNavigate({ kind: 'page', handle: pageMatch[1] });
      return;
    }

    // Fallback: pathname desconhecido
    onNavigate({ kind: 'unknown', href: pathname + url.search });
  };

  doc.addEventListener('click', listener, true);
  return () => doc.removeEventListener('click', listener, true);
}

export function setDeviceFrame(_iframe: HTMLIFrameElement, _device: Device) {
  // Chrome is now rendered by the Preview React component (phone bezel wrapper).
  // This helper is kept for compatibility but is a no-op.
}
